import { Input, InputProps } from '@chakra-ui/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface SearchInputProps extends Omit<InputProps, 'onChange' | 'value'> {
  onSearch: (term: string) => void
  value?: string
  debounceMs?: number
}

const SearchInput: React.FC<SearchInputProps> = ({ onSearch, value: externalValue, debounceMs = 300, ...inputProps }) => {
  const { t } = useTranslation()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [localValue, setLocalValue] = useState(externalValue ?? '')

  useEffect(() => {
    if (externalValue !== undefined) {
      setLocalValue(externalValue)
    }
  }, [externalValue])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setLocalValue(value)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => onSearch(value), debounceMs)
    },
    [onSearch, debounceMs],
  )

  return (
    <Input
      placeholder={t('common.labels.searchPlaceholder')}
      value={localValue}
      onChange={handleChange}
      size="sm"
      borderRadius="8px"
      border="1px solid"
      borderColor="solid.100"
      _focus={{
        borderColor: 'primary.100',
        boxShadow: '0 0 0 1px rgba(93,94,203,0.15)',
      }}
      fontSize="13px"
      h="32px"
      px="14px"
      fontWeight="600"
      minW={0}
      flexShrink={0}
      w={{ base: '100%', md: '100%' }}
      maxW={{ base: '100%', md: '280px' }}
      {...inputProps}
    />
  )
}

export default SearchInput
